/**
 * Combine all reducers in this file and export the combined reducers.
 */
import { combineReducers } from 'redux';
import { connectRouter } from 'connected-react-router/immutable';

import history from 'utils/history';

import uiStateReducer, { UISTATE_REDUCER_KEY } from 'components/UIState/reducer';
import ffReducer, { REDUCER_KEY as FF_REDUCER_KEY } from 'utils/featureFlags/reducer';

import accountsReducer, { REDUCER_KEY as ACCOUNTS_REDUCER_KEY } from 'data/accounts/reducer';
import accountDiscoveryReducer, { REDUCER_KEY as ACCOUNT_DISCOVERY_REDUCER_KEY } from 'components/Dialogs/AccountDiscovery/reducer';
import appReducer, { REDUCER_KEY as APP_REDUCER_KEY } from 'data/app/reducer';
import authReducer, { REDUCER_KEY as AUTH_REDUCER_KEY } from 'utils/auth/reducer';
import budgetsReducer, { BUDGETS_REDUCER_KEY } from 'data/budgets/budgetsReducer';
import budgetItemsReducer, { BUDGET_ITEMS_REDUCER_KEY } from 'data/budgetItems/budgetItemsReducer';
import categoriesReducer, { REDUCER_KEY as CATEGORIES_REDUCER_KEY } from 'data/categories/reducer';
import categoryGroupListsReducer, { CATEGORY_GROUP_LISTS_REDUCER_KEY } from 'data/categoryGroupLists/categoryGroupListsReducer';
import categoryGroupsReducer, { CATEGORY_GROUPS_REDUCER_KEY } from 'data/categoryGroups/categoryGroupsReducer';
import clipboardReducer, { REDUCER_KEY as CLIPBOARD_REDUCER_KEY } from 'data/clipboard/reducer';
import datasetsReducer, { REDUCER_KEY as DATASETS_REDUCER_KEY } from 'data/datasets/reducer';
import documentsReducer, { REDUCER_KEY as DOCUMENTS_REDUCER_KEY } from 'data/documents/reducer';
import filterReducer, { REDUCER_KEY as FILTER_REDUCER_KEY } from 'data/filters/reducer';
import institutionsReducer, { REDUCER_KEY as INSTITUTIONS_REDUCER_KEY } from 'data/institutions/reducer';
import institutionLoginsReducer, { REDUCER_KEY as INSTITUTION_LOGINS_REDUCER_KEY } from 'data/institutionLogins/reducer';
import investmentHoldingsReducer, { INVESTMENT_HOLDINGS_REDUCER_KEY } from 'data/investmentHoldings/reducer';
import investmentHoldingsV2Reducer, { INVESTMENT_HOLDINGS_V2_REDUCER_KEY } from 'data/investmentHoldingsV2/investmentHoldingsV2Reducer';
import memorizedRulesReducer, { REDUCER_KEY as MEMORIZED_RULES_REDUCER_KEY } from 'data/memorizedRules/reducer';
import persistReducer, { REDUCER_KEY as PERSIST_REDUCER_KEY } from 'data/persist/reducer';
import preferencesReducer, { REDUCER_KEY as PREFERENCES_REDUCER_KEY } from 'data/preferences/reducer';
import profileReducer, { REDUCER_KEY as PROFILE_REDUCER_KEY } from 'data/profile/reducer';
import rulesReducer, { REDUCER_KEY as RULES_REDUCER_KEY } from 'data/renameRules/reducer';
import rootUiReducer, { REDUCER_KEY as ROOTUI_REDUCER_KEY } from 'data/rootUi/reducer';
import spendingWatchListReducer, { REDUCER_KEY as SPENDING_WATCH_LIST_REDUCER_KEY } from 'data/spendingWatchList/reducer';
import stdFormReducer, { REDUCER_KEY as STD_FORM_DATA_REDUCER_KEY } from 'data/StdForm/reducer';
import subscriptionReducer, { SUBSCRIPTIONS_REDUCER_KEY } from 'data/subscriptions/subscriptionsReducer';
import tagsReducer, { REDUCER_KEY as TAGS_REDUCER_KEY } from 'data/tags/reducer';
import transactionsReducer, { REDUCER_KEY as TRANSACTIONS_REDUCER_KEY } from 'data/transactions/reducer';
import tripsReducer, { REDUCER_KEY as TRIPS_REDUCER_KEY } from 'data/trips/reducer';
import payeesReducer, { REDUCER_KEY as PAYEES_REDUCER_KEY } from 'data/payees/reducer';
import postponedActionsReducer, { POSTPONED_ACTIONS_REDUCER_KEY } from 'data/postponedActions/postponedActionsReducer';
import scheduledTransactionsReducer, { SCHEDULED_TRANSACTIONS_REDUCER_KEY } from 'data/scheduledTransactions/scheduledTransactionsReducer';
import undoReducer, { UNDO_REDUCER_KEY } from 'data/undo/undoReducer';
import freeToSpendReducer, { FREE_TO_SPEND_REDUCER_KEY } from 'data/freeToSpend/freeToSpendReducer';
import entitlemtnsReducer, { ENTITLEMENTS_REDUCER_KEY } from 'data/entitlements/entitlementsReducer';
import investmentBalancesV2Reducer, { INVESTMENT_BALANCES_V2_REDUCER_KEY } from 'data/investmentBalancesV2/investmentBalancesV2Reducer';
import investmentPerformanceV2Reducer, { INVESTMENT_PERFORMANCE_V2_REDUCER_KEY } from 'data/investmentPerformanceV2/investmentPerformanceV2Reducer';
import investmentQuotesV2Reducer, { INVESTMENT_QUOTES_V2_REDUCER_KEY } from 'data/investmentQuotesV2/investmentQuotesV2Reducer';
import { setRoot, hardReset } from 'data/actions';
import mcRulesReducer, { MC_RULES_REDUCER_KEY } from 'data/mcRules/mcRulesReducer';
import notificationsReducer, { NOTIFICATIONS_REDUCER_KEY } from 'data/notifications/notificationsReducer';
import goalsReducer, {REDUCER_KEY as GOALS_REDUCER_KEY} from 'data/goals/reducer';
import alertRulesReducer, { ALERT_RULES_REDUCER_KEY } from 'data/alertRules/reducer';
import alertsReducer, { ALERTS_REDUCER_KEY } from 'data/alerts/alertsReducer';
import webSocketQCSReducer, { WEB_SOCKET_QCS_REDUCER_KEY } from 'data/webSocketQCS/webSocketQCSReducer';
import investmentSymbolsReducer, { INVESTMENT_SYMBOLS_KEY } from 'data/investmentSymbols/investmentSymbolsReducer';
import vehiclesReducer, { REDUCER_KEY as VEHICLES_REDUCER_KEY } from 'data/vehicles/reducer';

/*
 * routeReducer
 *
 * The reducer merges route location changes into our immutable state.
 * The change is necessitated by moving to react-router-redux@5
 *
 */

// Combine Global App Reducers. The injectedReducers argument is required to
// suuport hot-loading for dynamically loaded reducers.
//
const combineAppReducers = (injectedReducers) => {
  const reducers = {
    router: connectRouter(history),
    [UISTATE_REDUCER_KEY]: uiStateReducer,
    [FF_REDUCER_KEY]: ffReducer,
    [ACCOUNT_DISCOVERY_REDUCER_KEY]: accountDiscoveryReducer,
    [ACCOUNTS_REDUCER_KEY]: accountsReducer,
    [APP_REDUCER_KEY]: appReducer,
    [AUTH_REDUCER_KEY]: authReducer,
    [BUDGETS_REDUCER_KEY]: budgetsReducer,
    [BUDGET_ITEMS_REDUCER_KEY]: budgetItemsReducer,
    [DATASETS_REDUCER_KEY]: datasetsReducer,
    [DOCUMENTS_REDUCER_KEY]: documentsReducer,
    [CATEGORIES_REDUCER_KEY]: categoriesReducer,
    [CATEGORY_GROUP_LISTS_REDUCER_KEY]: categoryGroupListsReducer,
    [CATEGORY_GROUPS_REDUCER_KEY]: categoryGroupsReducer,
    [CLIPBOARD_REDUCER_KEY]: clipboardReducer,
    [FILTER_REDUCER_KEY]: filterReducer,
    [INSTITUTION_LOGINS_REDUCER_KEY]: institutionLoginsReducer,
    [INSTITUTIONS_REDUCER_KEY]: institutionsReducer,
    [INVESTMENT_HOLDINGS_REDUCER_KEY]: investmentHoldingsReducer,
    [INVESTMENT_HOLDINGS_V2_REDUCER_KEY]: investmentHoldingsV2Reducer,
    [MEMORIZED_RULES_REDUCER_KEY]: memorizedRulesReducer,
    [PERSIST_REDUCER_KEY]: persistReducer,
    [PREFERENCES_REDUCER_KEY]: preferencesReducer,
    [PROFILE_REDUCER_KEY]: profileReducer,
    [RULES_REDUCER_KEY]: rulesReducer,
    [ROOTUI_REDUCER_KEY]: rootUiReducer,
    [SPENDING_WATCH_LIST_REDUCER_KEY]: spendingWatchListReducer,
    [STD_FORM_DATA_REDUCER_KEY]: stdFormReducer,
    [SUBSCRIPTIONS_REDUCER_KEY]: subscriptionReducer,
    [TAGS_REDUCER_KEY]: tagsReducer,
    [TRANSACTIONS_REDUCER_KEY]: transactionsReducer,
    [TRIPS_REDUCER_KEY]: tripsReducer,
    [PAYEES_REDUCER_KEY]: payeesReducer,
    [POSTPONED_ACTIONS_REDUCER_KEY]: postponedActionsReducer,
    [SCHEDULED_TRANSACTIONS_REDUCER_KEY]: scheduledTransactionsReducer,
    [UNDO_REDUCER_KEY]: undoReducer,
    [VEHICLES_REDUCER_KEY]: vehiclesReducer,
    [FREE_TO_SPEND_REDUCER_KEY]: freeToSpendReducer,
    [ENTITLEMENTS_REDUCER_KEY]: entitlemtnsReducer,
    [INVESTMENT_BALANCES_V2_REDUCER_KEY]: investmentBalancesV2Reducer,
    [INVESTMENT_PERFORMANCE_V2_REDUCER_KEY]: investmentPerformanceV2Reducer,
    [INVESTMENT_QUOTES_V2_REDUCER_KEY]: investmentQuotesV2Reducer,
    [MC_RULES_REDUCER_KEY]: mcRulesReducer,
    [NOTIFICATIONS_REDUCER_KEY]: notificationsReducer,
    [GOALS_REDUCER_KEY]: goalsReducer,
    [ALERT_RULES_REDUCER_KEY]: alertRulesReducer,
    [ALERTS_REDUCER_KEY]: alertsReducer,
    [WEB_SOCKET_QCS_REDUCER_KEY]: webSocketQCSReducer,
    [INVESTMENT_SYMBOLS_KEY]: investmentSymbolsReducer,
    ...injectedReducers,
  };
  return combineReducers(Object.keys(reducers).sort().reduce((reducersSorted, key) => { reducersSorted[key] = reducers[key]; return reducersSorted; }, {})); // eslint-disable-line no-param-reassign
}

// Create a Root Reducer which wraps and delegates to the App Reducers. The
// root reducer is needed to handle resetting of the redux store when a user
// logs out.
//
export default function createReducer(injectedReducers) {
  const appReducers = combineAppReducers(injectedReducers);
  return (state, action) => {
    switch(action.type) {
      case setRoot.toString():
        return action.payload;

      case hardReset.toString():
        return appReducers({
          [AUTH_REDUCER_KEY]: state[AUTH_REDUCER_KEY],
          [FF_REDUCER_KEY]: state[FF_REDUCER_KEY],
        }, action);

      default:
        return appReducers(state, action);
    }
  };
}

